import {
  Button,
  Card,
  CardContent,
  Checkbox,
  ConfirmAction,
  EmptyState,
  Input,
  Label,
  ListPage,
  RefreshButton,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { useApiClient, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { ChevronDown, ChevronRight, Plus, Power, RefreshCcw, Send, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { createWebhookDeliveriesApi } from '../api/webhookDeliveries.api';
import type { WebhookEntity } from '../api/webhooks.api';
import { webhooksApi } from '../api/webhooks.api';
import { appScopedQuery, useSelectedApp } from '../context/SelectedApp';
import type { WebhookDelivery } from '../schemas/webhookDelivery.schema';

const EVENT_OPTIONS = [
  'notification.requested',
  'notification.policy.rejected',
  'notification.queued',
  'notification.processing',
  'notification.delivered',
  'notification.failed',
  'notification.retrying',
  'notification.budget.exhausted',
  'webhook.endpoint.disabled',
  'webhook.endpoint.reactivated',
];

function DeliveryLog({ webhookId }: { webhookId: string }) {
  const deliveriesApi = useMemo(() => createWebhookDeliveriesApi(webhookId), [webhookId]);
  const { data, loading, error } = deliveriesApi.useList();
  const deliveries: WebhookDelivery[] = data ?? [];

  if (error) {
    return (
      <div className="p-3 text-sm text-destructive">
        Failed to load deliveries: {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  return (
    <div className="bg-muted/30 px-4 py-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event type</TableHead>
            <TableHead>Status code</TableHead>
            <TableHead>Success</TableHead>
            <TableHead>Attempt</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <TableRow key={`s-${i}`}>
                {Array.from({ length: 5 }).map((__, j) => (
                  <TableCell key={`s-${i}-${j}`}>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : deliveries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                No deliveries yet.
              </TableCell>
            </TableRow>
          ) : (
            deliveries.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-mono text-xs">{d.eventType}</TableCell>
                <TableCell>{d.statusCode ?? '—'}</TableCell>
                <TableCell>
                  <StatusBadge tone={d.success ? 'success' : 'danger'}>
                    {d.success ? 'Yes' : 'No'}
                  </StatusBadge>
                </TableCell>
                <TableCell>{d.attempt}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(d.createdAt).toLocaleString()}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function WebhooksPage() {
  const { connector } = useApiClient();
  const { showSnackbar } = useSnackbar();
  // Los webhooks son por app: sin `appId` el listado mezcla todas y el alta
  // responde 403 APP_CONTEXT_REQUIRED. La app sale del selector del header.
  const { appId, isUnset } = useSelectedApp();
  const scopedApi = webhooksApi.withQuery(appScopedQuery(appId));
  const { data: webhooksData, loading, error, refetch } = scopedApi.useList();
  const { mutate: createWebhook, loading: creating } = scopedApi.useCreate();
  const { mutate: deleteWebhook } = scopedApi.useDelete();

  const webhooks: WebhookEntity[] =
    (webhooksData as { data?: WebhookEntity[] })?.data ??
    (Array.isArray(webhooksData) ? webhooksData : []);

  const [showForm, setShowForm] = useState(false);
  const [formUrl, setFormUrl] = useState('');
  const [formSecret, setFormSecret] = useState('');
  const [formEvents, setFormEvents] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleEvent = (event: string) =>
    setFormEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );

  const resetForm = () => {
    setShowForm(false);
    setFormUrl('');
    setFormSecret('');
    setFormEvents([]);
  };

  const handleCreate = async () => {
    if (!formUrl || !formSecret || formSecret.length < 16 || formEvents.length === 0) return;
    try {
      await createWebhook({ url: formUrl, secret: formSecret, events: formEvents });
      showSnackbar({ message: 'Webhook created', variant: 'success' });
      resetForm();
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to create webhook', variant: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWebhook(id);
      showSnackbar({ message: 'Webhook deleted', variant: 'success' });
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to delete webhook', variant: 'error' });
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await connector.post(`webhooks/${id}/reactivate`, {});
      showSnackbar({ message: 'Webhook reactivated', variant: 'success' });
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to reactivate webhook', variant: 'error' });
    }
  };

  const handleTest = async (id: string) => {
    try {
      await connector.post(`webhooks/${id}/test`, {});
      showSnackbar({ message: 'Test event sent', variant: 'success' });
    } catch {
      showSnackbar({ message: 'Failed to send test event', variant: 'error' });
    }
  };

  return (
    <ListPage
      title="Webhooks"
      description="Receive event callbacks for delivery and webhook lifecycle events."
      actions={
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={refetch} isRefreshing={loading} />
          {/* Sin app elegida el alta responderia 403: se deshabilita con el
            motivo a la vista, en vez de dejar que el operador llene el
            formulario para recibir un error al final. */}
          <Button
            onClick={() => setShowForm((v) => !v)}
            disabled={isUnset}
            title={isUnset ? 'Elegí una app en el header para crear un webhook' : undefined}
          >
            <Plus /> {showForm ? 'Hide form' : 'Create webhook'}
          </Button>
        </div>
      }
    >
      {showForm ? (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="webhook-url">URL *</Label>
                <Input
                  id="webhook-url"
                  value={formUrl}
                  onChange={(e) => setFormUrl(e.target.value)}
                  placeholder="https://example.com/webhook"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="webhook-secret">Secret *</Label>
                <Input
                  id="webhook-secret"
                  type="password"
                  value={formSecret}
                  onChange={(e) => setFormSecret(e.target.value)}
                  placeholder="whsec_… (openssl rand -base64 32; min 16 chars)"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Events *</Label>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {EVENT_OPTIONS.map((ev) => (
                  <label key={ev} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={formEvents.includes(ev)}
                      onCheckedChange={() => toggleEvent(ev)}
                    />
                    <span className="font-mono text-xs">{ev}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleCreate}
                disabled={!formUrl || formSecret.length < 16 || formEvents.length === 0 || creating}
              >
                {creating ? 'Creating…' : 'Create webhook'}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {error ? (
        <EmptyState
          title="Couldn't load webhooks"
          description={error instanceof Error ? error.message : 'Unexpected error.'}
          action={<Button onClick={() => refetch()}>Retry</Button>}
          className="border-destructive/40 bg-destructive/5"
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Failures</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`loading-${i}`}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={`loading-${i}-${j}`}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : webhooks.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    No webhooks configured yet.
                  </TableCell>
                </TableRow>
              ) : (
                webhooks.map((wh) => (
                  <React.Fragment key={wh.id}>
                    <TableRow>
                      <TableCell className="max-w-[260px] truncate font-mono text-xs">
                        {wh.url}
                      </TableCell>
                      <TableCell>
                        <span className="block max-w-[260px] truncate text-xs text-muted-foreground">
                          {wh.events.join(', ')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge tone={wh.isActive ? 'success' : 'danger'}>
                          {wh.isActive ? 'Active' : 'Inactive'}
                        </StatusBadge>
                      </TableCell>
                      <TableCell
                        className={
                          wh.failureCount > 0
                            ? 'text-right font-semibold text-destructive'
                            : 'text-right text-muted-foreground'
                        }
                      >
                        {wh.failureCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex flex-wrap justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleTest(wh.id)}>
                            <Send /> Test
                          </Button>
                          {!wh.isActive ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-success hover:text-success"
                              onClick={() => handleReactivate(wh.id)}
                            >
                              <Power /> Reactivate
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedId(expandedId === wh.id ? null : wh.id)}
                          >
                            {expandedId === wh.id ? <ChevronDown /> : <ChevronRight />}
                            {expandedId === wh.id ? 'Hide log' : 'Deliveries'}
                          </Button>
                          <ConfirmAction
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 /> Delete
                              </Button>
                            }
                            title="Delete this webhook?"
                            description={`This permanently deletes the webhook for ${wh.url}.`}
                            confirmLabel="Delete"
                            destructive
                            onConfirm={() => handleDelete(wh.id)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedId === wh.id ? (
                      <TableRow>
                        <TableCell colSpan={5} className="p-0">
                          <DeliveryLog webhookId={wh.id} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        <RefreshCcw className="mr-1 inline h-3 w-3" /> Webhooks auto-disable after 10 consecutive
        failures. Reactivate manually once your endpoint is healthy.
      </p>
    </ListPage>
  );
}
